
import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { Info } from "lucide-react";

const PreferencesTab = () => {
  const { toast } = useToast();
  
  const emailCategories = [
    {
      label: "Your Turn",
      color: "#fb4c2f",
      description: "Emails where it's your turn to respond and action is required."
    },
    {
      label: "Their Turn",
      color: "#ffad47",
      description: "Messages you've replied to and are now waiting on a response from the other party."
    },
    {
      label: "Opportunity",
      color: "#fad165",
      description: "Paid brand deal inquiries with rates, deliverables, or media kit requests."
    },
    {
      label: "Payments",
      color: "#16a766",
      description: "Invoices, receipts, billing confirmations, and related financial documents."
    },
    {
      label: "FYI",
      color: "#43d692",
      description: "Informational emails that don't need a reply but may still be useful."
    },
    {
      label: "PR",
      color: "#4a86e8",
      description: "Gifting, event invites, press, or PR outreach with no deliverables required."
    },
    {
      label: "Notifications",
      color: "#a479e2",
      description: "Auto-emails like platform updates, newsletters, or service alerts."
    },
    {
      label: "Team",
      color: "#f691b3",
      description: "Internal emails sent by team members using your shared domain."
    },
    {
      label: "Needs Review",
      color: "#cccccc",
      description: "Unclear or suspicious messages, including affiliate or commission-based outreach."
    }
  ];

  // Initialize all switches as unchecked (false)
  const [switchStates, setSwitchStates] = useState<Record<string, boolean>>(
    emailCategories.reduce((acc, category) => {
      acc[category.label] = false;
      return acc;
    }, {} as Record<string, boolean>)
  );

  const handleSwitchChange = (categoryLabel: string, checked: boolean) => {
    setSwitchStates(prev => ({
      ...prev,
      [categoryLabel]: checked
    }));

    // Show purple toast notification
    toast({
      description: checked 
        ? `${categoryLabel} emails will now be hidden from your inbox`
        : `${categoryLabel} emails will now be visible in your inbox`,
      className: "bg-purple-500 text-white border-purple-600"
    });
  };

  return (
    <Card className="bg-white shadow-sm border border-gray-200">
      <CardHeader className="pb-4 px-4 lg:px-6">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Email categorization</h3>
            <p className="text-sm text-gray-600 mt-1">
              Automatically sort and filter your emails to keep your main inbox focused on important messages.
            </p>
          </div>
          <Switch defaultChecked />
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-4 lg:px-6">
        <TooltipProvider>
          {/* Headers */}
          <div className="hidden md:grid md:grid-cols-[100px_100px_1fr] gap-2 mb-4">
            <div className="text-sm text-gray-600 font-medium flex items-center gap-2">
              Hide
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Turn on to hide these emails from your inbox</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="text-sm text-gray-600 font-medium">
              Label
            </div>
            <div className="text-sm text-gray-600 font-medium">
              Description
            </div>
          </div>

          {/* Email Categories */}
          <div className="space-y-6 md:space-y-4">
            {emailCategories.map((category) => (
              <div key={category.label} className="md:grid md:grid-cols-[100px_100px_1fr] gap-2 md:items-center">
                {/* Mobile: Label first */}
                <div className="flex items-center mb-3 md:mb-0 md:order-2">
                  <span className="text-sm text-gray-600 font-medium md:hidden mr-3">Label:</span>
                  <div 
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-black whitespace-nowrap" 
                    style={{ backgroundColor: category.color }}
                  >
                    {category.label}
                  </div>
                </div>

                {/* Mobile: Description second */}
                <div className="text-sm text-gray-600 mb-3 md:mb-0 md:order-3">
                  <span className="font-medium md:hidden">Description: </span>
                  {category.description}
                </div>

                {/* Mobile: Hide toggle third, Desktop: Hide toggle first */}
                <div className="flex items-center md:order-1">
                  <span className="text-sm text-gray-600 font-medium md:hidden mr-3 min-w-fit flex items-center gap-2">
                    Hide:
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">Turn on to hide these emails from your inbox</p>
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <div className="w-[100px] flex justify-start">
                    <Switch 
                      checked={switchStates[category.label]} 
                      onCheckedChange={(checked) => handleSwitchChange(category.label, checked)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

export default PreferencesTab;
